import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { toast } from 'react-toastify'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  getSupportPractitionerCommunicationGates,
  saveSupportPractitionerCommunicationGates,
} from 'src/supportPortal/services/practitionerOnboardingService'
import {
  getSupportPractitionerCommunicationsBootstrap,
  sendSupportPractitionerGenericCommunication,
  sendSupportPractitionerPayoutReportCommunication,
  sendSupportPractitionerTherapyReportReminder,
} from 'src/supportPortal/services/customCommunicationService'
import {
  COMMUNICATION_GATE_THERAPY_REPORT_COMPLETION_REMINDER,
  PAYOUT_CYCLE_COMMUNICATION_GATE_ITEMS,
  buildPractitionerCommunicationGateSettings,
  getPractitionerCommunicationGateSettings,
} from 'src/supportPortal/utils/practitionerCommunicationGateSettings'
import { getMinimalMentorData } from 'src/supportPortal/views/therapists/util'

const DEFAULT_MODES = ['whatsapp']

function getPractitionerName(practitioner) {
  return (
    practitioner?.name ||
    practitioner?.fullName ||
    practitioner?.displayName ||
    [practitioner?.firstName, practitioner?.lastName].filter(Boolean).join(' ') ||
    'Therapist'
  )
}

function buildTargetPayload(practitioner, mentorSlug) {
  return {
    practitionerId: practitioner?.id,
    practitionerSlug: practitioner?.slug || mentorSlug,
  }
}

function DispatchResult({ result }) {
  if (!result) return null

  const dispatches = Array.isArray(result?.dispatches) ? result.dispatches : []
  const reminderCounts = result?.counts

  return (
    <Alert severity={result?.sent === false ? 'info' : 'success'} variant="outlined" sx={{ borderRadius: 2 }}>
      <Stack spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {result?.reason || 'Communication request completed.'}
        </Typography>
        {result?.invoiceNumber && (
          <Typography variant="caption" color="text.secondary">
            Invoice: {result.invoiceNumber}
          </Typography>
        )}
        {result?.reportUrl && (
          <Box>
            <Button component="a" href={result.reportUrl} target="_blank" rel="noreferrer" size="small">
              Open PDF
            </Button>
          </Box>
        )}
        {dispatches.length > 0 && (
          <Stack spacing={0.5}>
            {dispatches.map((item) => (
              <Typography key={`${item?.mode}-${item?.status}`} variant="caption" color="text.secondary">
                {item?.mode}: {item?.message || item?.status}
              </Typography>
            ))}
          </Stack>
        )}
        {reminderCounts?.overall?.label && (
          <Typography variant="caption" color="text.secondary">
            Overall pending: {reminderCounts.overall.label}
          </Typography>
        )}
      </Stack>
    </Alert>
  )
}

function getFailedDispatches(data) {
  const dispatches = Array.isArray(data?.dispatches) ? data.dispatches : []
  return dispatches.filter((item) => item?.status === 'failed')
}

function formatFailedDispatches(failedDispatches) {
  return failedDispatches
    .map((item) => `${item?.mode || 'channel'}: ${item?.message || item?.status || 'failed'}`)
    .join('; ')
}

export default function TherapistCommunicationsPage() {
  const { mentorSlug } = useParams()
  const [therapist, setTherapist] = useState(null)
  const [bootstrap, setBootstrap] = useState(null)
  const [gateSettings, setGateSettings] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [savingGateKey, setSavingGateKey] = useState('')
  const [sendingKey, setSendingKey] = useState('')
  const [selectedModes, setSelectedModes] = useState(DEFAULT_MODES)
  const [subject, setSubject] = useState('Message from AUMHUM')
  const [message, setMessage] = useState('')
  const [result, setResult] = useState(null)

  const therapistName = getPractitionerName(therapist)
  const targetPayload = useMemo(() => buildTargetPayload(therapist, mentorSlug), [mentorSlug, therapist])
  const gateValues = gateSettings || getPractitionerCommunicationGateSettings(therapist)
  const modeOptions = useMemo(() => {
    const options = Array.isArray(bootstrap?.communicationModes) ? bootstrap.communicationModes : []
    return options.length
      ? options
      : [
          { id: 'call', label: 'Call', configured: true },
          { id: 'whatsapp', label: 'WhatsApp', configured: true },
          { id: 'email', label: 'Email', configured: true },
        ]
  }, [bootstrap])
  const queuedWhatsappConfigured = bootstrap?.queuedWhatsapp?.openerTemplateSidConfigured !== false

  useEffect(() => {
    let active = true
    async function load() {
      setLoading(true)
      setLoadError('')
      setResult(null)
      try {
        const therapistData = await getMinimalMentorData(
          mentorSlug,
          true,
          `/api/secure/cc/support/therapists/by-slug?mentor-slug=${mentorSlug}`,
        )
        if (!active) return
        setTherapist(therapistData || null)

        const payload = buildTargetPayload(therapistData, mentorSlug)
        const [bootstrapResponse, gateResponse] = await Promise.all([
          getSupportPractitionerCommunicationsBootstrap(payload),
          getSupportPractitionerCommunicationGates(payload),
        ])
        if (!active) return

        if (bootstrapResponse?.ok) {
          setBootstrap(bootstrapResponse.data || null)
        } else {
          setLoadError(bootstrapResponse?.message || 'Unable to load communication options.')
        }

        if (gateResponse?.ok) {
          setGateSettings(getPractitionerCommunicationGateSettings(gateResponse.data || {}))
        }
      } catch (error) {
        console.error('Error loading therapist communications:', error)
        if (active) {
          setLoadError('Unable to load therapist communications.')
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      active = false
    }
  }, [mentorSlug])

  const handleModeToggle = (modeId) => {
    setSelectedModes((previous) => {
      if (previous.includes(modeId)) {
        return previous.filter((item) => item !== modeId)
      }
      return [...previous, modeId]
    })
  }

  const handleGateChange = async (gateKey, enabled, labels = {}) => {
    if (!therapist?.id) return

    const previousSettings = gateSettings
    const nextSettings = buildPractitionerCommunicationGateSettings(gateValues, gateKey, enabled)
    setGateSettings(nextSettings)
    setSavingGateKey(gateKey)

    try {
      const response = await saveSupportPractitionerCommunicationGates(targetPayload, nextSettings)
      if (!response.ok) {
        setGateSettings(previousSettings)
        toast.error(response.message || 'Unable to save communication setting.')
        return
      }
      setGateSettings(
        getPractitionerCommunicationGateSettings(response.data || { communicationGateSettings: nextSettings }),
      )
      toast.success(enabled ? labels.enabled : labels.disabled)
    } catch (error) {
      console.error('Error saving communication gate:', error)
      setGateSettings(previousSettings)
      toast.error('Unable to save communication setting.')
    } finally {
      setSavingGateKey('')
    }
  }

  const handleSendReminder = async () => {
    if (!therapist?.id) return
    setSendingKey('therapy-report-reminder')
    setResult(null)
    const response = await sendSupportPractitionerTherapyReportReminder(targetPayload)
    setSendingKey('')

    if (response?.ok) {
      setResult(response.data || null)
      if (response?.data?.sent === false) {
        toast.info(response?.data?.reason || 'No reminder was sent.')
      } else {
        toast.success('Therapy report reminder sent.')
      }
    } else {
      toast.error(response?.message || 'Unable to send therapy report reminder.')
    }
  }

  const handleSendPayoutReport = async () => {
    if (!therapist?.id) return
    setSendingKey('payout-report')
    setResult(null)
    const response = await sendSupportPractitionerPayoutReportCommunication(targetPayload)
    setSendingKey('')

    if (response?.ok) {
      setResult(response.data || null)
      if (response?.data?.sent === false) {
        toast.info(response?.data?.reason || 'No payout report was sent.')
      } else {
        toast.success('Payout report communication sent.')
      }
    } else {
      toast.error(response?.message || 'Unable to send payout report communication.')
    }
  }

  const handleSendGeneric = async () => {
    const trimmedMessage = message.trim()
    if (!selectedModes.length) {
      toast.error('Select at least one communication mode.')
      return
    }
    if (!trimmedMessage) {
      toast.error('Enter a message before sending.')
      return
    }

    setSendingKey('generic')
    setResult(null)
    const response = await sendSupportPractitionerGenericCommunication({
      ...targetPayload,
      modes: selectedModes,
      subject: subject.trim() || 'Message from AUMHUM',
      message: trimmedMessage,
    })
    setSendingKey('')

    if (response?.ok) {
      setResult(response.data || null)
      const dispatches = Array.isArray(response?.data?.dispatches) ? response.data.dispatches : []
      const failedDispatches = getFailedDispatches(response.data)
      if (dispatches.length > 0 && failedDispatches.length === dispatches.length) {
        toast.error('No communication channel could be sent.')
      } else if (failedDispatches.length > 0) {
        toast.warn(formatFailedDispatches(failedDispatches) || 'Some communication channels failed.')
      } else {
        toast.success('Practitioner communication requested.')
      }
    } else {
      toast.error(response?.message || 'Unable to send practitioner communication.')
    }
  }

  const isSending = Boolean(sendingKey)

  return (
    <Box sx={{ minHeight: '100%', bgcolor: '#f8fafc' }}>
      <SupportHeader
        heading={`${therapistName} Communications`}
        subHeading="Send reminders, payout reports, and direct support messages."
      />
      <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
        {loading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}>
            <CircularProgress size={30} />
            <Typography variant="body2" color="text.secondary">
              Loading communication workspace...
            </Typography>
          </Stack>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : (
          <Stack spacing={2.5}>
            <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <CampaignOutlinedIcon color="primary" />
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 800 }}>
                    {therapistName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Scheduled gates are default-off for practitioners and also require the global constants to be enabled.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Automated Communication Gates
                </Typography>
                <FormControlLabel
                  sx={{ m: 0, alignItems: 'flex-start', gap: 1 }}
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(gateValues[COMMUNICATION_GATE_THERAPY_REPORT_COMPLETION_REMINDER])}
                      disabled={Boolean(savingGateKey)}
                      onChange={(event) =>
                        handleGateChange(
                          COMMUNICATION_GATE_THERAPY_REPORT_COMPLETION_REMINDER,
                          event.target.checked,
                          {
                            enabled: 'Therapy report reminders enabled.',
                            disabled: 'Therapy report reminders disabled.',
                          },
                        )
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Therapy Report Completion Reminders
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Sends only when pending reports or incomplete events could delay payouts.
                      </Typography>
                    </Box>
                  }
                />
                <Divider />
                <Stack spacing={0.75}>
                  {PAYOUT_CYCLE_COMMUNICATION_GATE_ITEMS.map((item) => (
                    <FormControlLabel
                      key={item.key}
                      sx={{ m: 0, alignItems: 'flex-start', gap: 1 }}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(gateValues[item.key])}
                          disabled={Boolean(savingGateKey)}
                          onChange={(event) =>
                            handleGateChange(item.key, event.target.checked, {
                              enabled: `${item.label} enabled.`,
                              disabled: `${item.label} disabled.`,
                            })
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {savingGateKey === item.key ? 'Saving setting...' : item.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </Stack>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DescriptionOutlinedIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Payout Report Communication
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Sends the latest completed-cycle payout report over the configured practitioner payout channels.
                  The backend generates or reuses the include-all-sessions PDF before sending.
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleSendPayoutReport}
                  disabled={isSending}
                  startIcon={
                    sendingKey === 'payout-report' ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />
                  }
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {sendingKey === 'payout-report' ? 'Sending...' : 'Send Payout Report'}
                </Button>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Therapy Report Reminder
                </Typography>
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  Sends only if this therapist has pending report work or events not marked complete. The message includes
                  yesterday, this week, this month, and overall counts, plus the payout-delay warning.
                </Alert>
                <Button
                  variant="contained"
                  onClick={handleSendReminder}
                  disabled={isSending}
                  startIcon={
                    sendingKey === 'therapy-report-reminder' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SendOutlinedIcon />
                    )
                  }
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {sendingKey === 'therapy-report-reminder' ? 'Sending...' : 'Send Report Reminder'}
                </Button>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Generic Communication
                </Typography>
                <FormGroup row>
                  {modeOptions.map((option) => (
                    <FormControlLabel
                      key={option?.id}
                      control={
                        <Checkbox
                          checked={selectedModes.includes(option?.id)}
                          disabled={isSending || option?.configured === false}
                          onChange={() => handleModeToggle(option?.id)}
                        />
                      }
                      label={option?.label || option?.id}
                    />
                  ))}
                </FormGroup>
                <Alert
                  severity={queuedWhatsappConfigured ? 'info' : 'warning'}
                  icon={<WhatsAppIcon />}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  {queuedWhatsappConfigured
                    ? 'If WhatsApp is outside the 24-hour window, the backend sends the important-message template and queues the actual text for 24 hours.'
                    : 'The WhatsApp cold-window opener template is not configured in this environment. Warm-window WhatsApp may still work, but queued delivery will fail until the template SID is deployed.'}
                </Alert>
                <TextField
                  label="Email Subject"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  disabled={isSending}
                  fullWidth
                />
                <TextField
                  label="Message"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  disabled={isSending}
                  minRows={5}
                  multiline
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handleSendGeneric}
                  disabled={isSending}
                  startIcon={sendingKey === 'generic' ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {sendingKey === 'generic' ? 'Sending...' : 'Send Communication'}
                </Button>
              </Stack>
            </Paper>

            <DispatchResult result={result} />
          </Stack>
        )}
      </Box>
    </Box>
  )
}
